import { Grid, Group, Paper, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useHeartRhythmMeasurementDetails } from 'hooks/api/useHeartRhythmData';
import { usePatient } from 'hooks/api/useUser';
import { CenteredLoading } from 'components/CenteredLoading';
import { shouldDisplayMeasurementFusdrBadge } from 'pages/Main/Patients/Patients.helpers';
import { FusdrBadge } from 'components/FusdrBadge';
import { showUsaFeature } from 'utils/app';
import { showDeviceVersion, showDeviceInfo } from 'utils/measurement';
import { useGroupNameById, useSymptomsName } from './MeasurementDetails.helpers';
import { convertSeverityToExtendedValue } from '../HeartRateCharts/HeartRateCharts.helpers';

interface MeasurementDetailsInfoProps {
  measurementId?: string;
}

export const MeasurementDetailsInfo = ({ measurementId }: MeasurementDetailsInfoProps) => {
  const { t } = useTranslation();
  const { data: selectedMeasurement } = useHeartRhythmMeasurementDetails({ measurementId });
  const groupNameById = useGroupNameById(selectedMeasurement?.groupIds);
  const symptomName = useSymptomsName(selectedMeasurement?.data?.context?.symptoms);
  const { data: patientData, isPending: isLoadingPatient } = usePatient({ patientId: selectedMeasurement?.creatorId });

  const renderFusdrBadge = shouldDisplayMeasurementFusdrBadge({
    measurementTags: selectedMeasurement?.data?.tags,
  });

  if (isLoadingPatient) return <CenteredLoading />;

  return (
    <>
      <Paper shadow={'sm'} radius={'md'} p={'md'} mb={'xl'} withBorder>
        <Grid gutter={'xs'}>
          <Grid.Col span={12}>
            <Grid>
              <Grid.Col span={3} fw={700}>
                {t('patient_info_label')}
              </Grid.Col>
              <Grid.Col span={3}>{`${patientData?.firstName} ${patientData?.lastName}`}</Grid.Col>
              <Grid.Col span={3} fw={700}>
                {t('patients_info_group')}
              </Grid.Col>
              <Grid.Col span={3}>{groupNameById}</Grid.Col>
              <Grid.Col span={3} fw={700}>
                {t('patients_info_symptoms')}
              </Grid.Col>
              <Grid.Col span={3}>{symptomName}</Grid.Col>
              <Grid.Col span={3} fw={700}>
                {t('patients_info_severity')}
              </Grid.Col>
              <Grid.Col span={3}>
                {convertSeverityToExtendedValue(selectedMeasurement?.data?.context?.symptomSeverity)}
              </Grid.Col>
              <Grid.Col span={3} fw={700}>
                {t('patients_info_activity')}
              </Grid.Col>
              <Grid.Col span={3}>
                {selectedMeasurement?.data?.context?.activity &&
                  t(`activity_${selectedMeasurement?.data?.context?.activity}`)}
              </Grid.Col>
              <Grid.Col span={3} fw={700}>
                {t('patients_info_status')}
              </Grid.Col>
              <Grid.Col span={3}>{t(`status_${selectedMeasurement?.status}`)}</Grid.Col>
              {showUsaFeature && (
                <>
                  <Grid.Col span={3} fw={700}>
                    {t('patients_appversion')}
                  </Grid.Col>
                  <Grid.Col span={3}>{showDeviceVersion(selectedMeasurement?.data)}</Grid.Col>
                  <Grid.Col span={3} fw={700}>
                    {t('patients_device')}
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Group>
                      <Text tt={'capitalize'}>{showDeviceInfo(selectedMeasurement?.data)}</Text>
                      {renderFusdrBadge && <FusdrBadge model={selectedMeasurement?.data?.device?.model} />}
                    </Group>
                  </Grid.Col>
                </>
              )}

              {selectedMeasurement?.data?.diagnosis?.text && (
                <>
                  <Grid.Col span={3} fw={700}>
                    {t('patients_info_remark')}
                  </Grid.Col>
                  <Grid.Col span={9}>{<Text>{selectedMeasurement?.data.diagnosis?.text}</Text>}</Grid.Col>
                </>
              )}
            </Grid>
          </Grid.Col>
        </Grid>
      </Paper>
    </>
  );
};
